'use client';

import { useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { CheckIcon, XIcon } from '@phosphor-icons/react';

export const CUSTOM_NAME_MAX_LENGTH = 120;

/**
 * Normalizes a rename input to the value the API stores (VER-377): trimmed, inner
 * whitespace collapsed, casing preserved. Returns null for blank — the CLEAR
 * operation ("Restaurar nome original") — and undefined for invalid (over-long).
 */
export function parseCustomNameInput(raw: string): string | null | undefined {
  const collapsed = raw.trim().replace(/\s+/g, ' ');
  if (collapsed.length === 0) return null;
  if (collapsed.length > CUSTOM_NAME_MAX_LENGTH) return undefined;
  return collapsed;
}

/**
 * The inline transaction-rename editor (VER-377) — the InlineLimitEditor pattern for
 * prose: seeded with the current display name, Enter commits, Escape cancels,
 * check/x for touch. Committing blank clears the custom name (restore).
 */
export function InlineNameEditor({
  initialValue,
  saving = false,
  commitDisabled = false,
  onCommit,
  onCancel,
}: {
  initialValue: string;
  saving?: boolean;
  /** Blocks commit (button AND Enter) without freezing typing/cancel — e.g. while a
   *  batch preview is loading or says the batch can't run. */
  commitDisabled?: boolean;
  /** null = clear ("Restaurar nome original"). */
  onCommit: (value: string | null) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(initialValue);
  const [invalid, setInvalid] = useState(false);

  const commit = () => {
    if (commitDisabled) return;
    const parsed = parseCustomNameInput(draft);
    if (parsed === undefined) {
      setInvalid(true); // keep the editor open, but say so instead of silently ignoring
      return;
    }
    onCommit(parsed);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInvalid(false);
    setDraft(event.target.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') commit();
    if (event.key === 'Escape') onCancel();
  };

  return (
    <span className="flex w-full min-w-0 items-center gap-1.5">
      <input
        autoFocus
        data-testid="rename-input"
        value={draft}
        maxLength={CUSTOM_NAME_MAX_LENGTH + 10}
        disabled={saving}
        aria-label="Renomear descrição"
        aria-invalid={invalid || undefined}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className="w-full min-w-0 rounded-md border border-border-brand bg-surface px-2.5 py-1.5 text-base font-semibold text-text-primary shadow-xs outline-none focus-visible:ring-[3px] focus-visible:ring-brand-200/60 aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20 sm:text-sm"
      />
      {invalid && (
        <span role="alert" className="sr-only">
          Nome muito longo.
        </span>
      )}
      <button
        type="button"
        data-testid="rename-commit"
        aria-label="Salvar nome"
        onClick={commit}
        disabled={saving || commitDisabled}
        className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary text-primary-foreground"
      >
        <CheckIcon className="size-3.5" />
      </button>
      <button
        type="button"
        data-testid="rename-cancel"
        aria-label="Cancelar edição do nome"
        onClick={onCancel}
        disabled={saving}
        className="grid size-8 shrink-0 place-items-center rounded-lg border border-border-subtle text-text-secondary"
      >
        <XIcon className="size-3.5" />
      </button>
    </span>
  );
}
